import { useCallback, useEffect, useState } from "react";
import { SupportingInfo } from "../supportingInfo";
import { MainSurveyStore } from "modules/MainSurveyEditor/hooks/useMainSurvey";
import { useSupportingInfoData } from "./useSupportingInfoData";
import { useMainSurveyData } from "modules/MainSurveyEditor/hooks/useMainSurveyData";
import { SurveyBlocks } from "modules/MainSurveyEditor/surveyBlocks";
import { relatedSupportingInfoClient } from "../client/relatedSupportingInfoClient";
import { supportingInfoClient } from "../client/supportingInfoClient";

export const useRelatedSupportingInfo = (mainSurveyStore: MainSurveyStore, isSubBlock: boolean) => {
  const { blockToEdit: block, setIsSaving, handleSurveySaved, subBlockToEdit: subBlock } = mainSurveyStore;

  const blockToEdit = (isSubBlock && subBlock) ? subBlock : block;

  const { surveyBlock, isLoading: isSurveyBlockLoading } = useMainSurveyData(
    blockToEdit?.parentSurveyId,
    blockToEdit?.id
  );
  const [relatedInfos, setRelatedInfos] = useState<SupportingInfo.PartialRelatedSupportingInfo[]>([]);

  const { supportingInfos: infoOptions, isLoadingInfos } = useSupportingInfoData({ limit: 1000, offset: 0 });

  const addSupportingInfo = useCallback(
    (info: SupportingInfo.PartialRelatedSupportingInfo = { supportingInformation: {} }) => {
      setRelatedInfos((old) => [...old, info]);
    },
    []
  );

  const handleSelectSupportingInfo = useCallback(
    async (index: number, infoId: number | string, info?: SupportingInfo.SupportingInfo) => {
      if (
        [
          String(relatedInfos[index].supportingInformation?.parent),
          String(relatedInfos[index].supportingInformation?.id),
        ].includes(String(infoId))
      ) {
        return;
      }

      setIsSaving(true);
      const temp = [...relatedInfos];
      const infoToUpdate = temp[index];
      let newInfo;

      try {
        if (infoToUpdate.id) {
          await relatedSupportingInfoClient.updateRelatedSupportingInfo(
            blockToEdit.parentSurveyId,
            blockToEdit.id!,
            infoToUpdate.id,
            { supportingInformation: infoId }
          );
          newInfo = {
            ...infoToUpdate,
            supportingInformation: info || infoOptions.find((info) => String(info.id) === String(infoId))!,
          };
        } else {
          const newInfoRes = await relatedSupportingInfoClient.addSupportingInfoToBlock(
            blockToEdit.parentSurveyId,
            blockToEdit.id!,
            infoId
          );
          newInfo = newInfoRes.data;
        }

        temp.splice(index, 1, newInfo);
        setRelatedInfos(temp);
        handleSurveySaved();
      } catch (e) {
        console.error(e);
        setIsSaving(false);
      }
    },
    [blockToEdit, handleSurveySaved, infoOptions, setIsSaving, relatedInfos]
  );

  const handleContentUpdate = useCallback(
    async (index: number, content: string) => {
      setIsSaving(true);
      const infoToUpdate = relatedInfos[index];
      if (!infoToUpdate.supportingInformation.id || infoToUpdate.supportingInformation.content === content) {
        setIsSaving(false);
        return;
      }

      if (!infoToUpdate.supportingInformation.parent) {
        const newInfo = await supportingInfoClient.createSupportingInfo({
          content,
          parent: infoToUpdate.supportingInformation.id,
          title: infoToUpdate.supportingInformation.title || "",
        });

        await handleSelectSupportingInfo(index, newInfo.data.id, newInfo.data);
      } else {
        setRelatedInfos((old) => {
          const temp = [...old];
          temp.splice(index, 1, {
            id: temp[index].id,
            supportingInformation: { ...temp[index].supportingInformation, content },
          });
          return temp;
        });

        await supportingInfoClient.updateSupportingInfo(infoToUpdate.supportingInformation.id, {
          content,
        });
      }
      handleSurveySaved();
    },
    [setIsSaving, relatedInfos, handleSurveySaved, handleSelectSupportingInfo]
  );

  const handleTabNameUpdate = useCallback(
    async (index: number, tabName: string | null) => {
      setIsSaving(true);
      const infoToUpdate = relatedInfos[index];
      if (!infoToUpdate.supportingInformation.id || infoToUpdate.tabName === tabName) {
        setIsSaving(false);
        return;
      }

      try {
        await relatedSupportingInfoClient.updateRelatedSupportingInfo(
          blockToEdit.parentSurveyId,
          blockToEdit.id!,
          infoToUpdate.id!,
          { tabName }
        );
        setRelatedInfos((old) => {
          const temp = [...old];
          temp[index].tabName = tabName;
          return temp;
        });

        handleSurveySaved();
      } catch (e) {
        console.error(e);
        setIsSaving(false);
      }
    },
    [setIsSaving, relatedInfos, handleSurveySaved, blockToEdit]
  );

  const handleDeleteInfo = useCallback(
    async (index: number) => {
      setIsSaving(true);
      const infoToDelete = relatedInfos[index];
      setRelatedInfos((old) => {
        const temp = [...old];
        temp.splice(index, 1);
        return temp;
      });
      if (infoToDelete.id) {
        await relatedSupportingInfoClient.deleteSupportingInfoFromBlock(
          blockToEdit.parentSurveyId,
          blockToEdit.id!,
          infoToDelete.id
        );
      }
      handleSurveySaved();
    },
    [blockToEdit, handleSurveySaved, setIsSaving, relatedInfos]
  );

  useEffect(() => {
    if (!surveyBlock || isSurveyBlockLoading) {
      return;
    }

    setRelatedInfos((surveyBlock as SurveyBlocks.Block).relatedSupportingInformation || []);
  }, [isSurveyBlockLoading, surveyBlock]);

  return {
    relatedInfos,
    setRelatedInfos,
    infoOptions: infoOptions,
    isLoadingInfoOptions: isLoadingInfos,
    addSupportingInfo,
    handleSelectSupportingInfo,
    handleContentUpdate,
    handleTabNameUpdate,
    handleDeleteInfo,
  };
};
